import { useEffect, useState, useCallback, useRef } from 'react';
import type { Card as CardType } from '@flip7/shared';
import { Card } from './Card';

interface CardRevealProps {
  card: CardType;
  isBust: boolean;
  playerName: string;
  onComplete: () => void;
}

/**
 * Bust-only overlay — shows the bust card with dramatic red glow.
 * No 3D flip (the inline FlippableCard already revealed the card).
 * 800ms display + 200ms exit.
 */
export function CardReveal({ card, isBust, playerName, onComplete }: CardRevealProps) {
  const [phase, setPhase] = useState<'showing' | 'exiting'>('showing');
  const completedRef = useRef(false);

  const fireComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }, [onComplete]);

  const dismiss = useCallback(() => {
    if (phase === 'exiting') return;
    setPhase('exiting');
    setTimeout(fireComplete, 200);
  }, [phase, fireComplete]);

  useEffect(() => {
    const exitTimer = setTimeout(() => setPhase('exiting'), 800);
    const doneTimer = setTimeout(fireComplete, 1000);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [fireComplete]);

  return (
    <div
      className={`card-reveal-overlay ${isBust ? 'card-reveal-overlay--bust' : 'card-reveal-overlay--normal'}${phase === 'exiting' ? ' exiting' : ''}`}
      onClick={dismiss}
    >
      <div className={`card-reveal__card-wrap ${isBust ? 'card-reveal__card-wrap--bust' : 'card-reveal__card-wrap--normal'}`}>
        <div className="card-reveal__card-scale">
          <Card card={card} />
        </div>
      </div>

      <div className="card-reveal__text">
        {isBust && <p className="card-reveal__bust-label">BUST!</p>}
        <p className="card-reveal__player-name">{playerName}</p>
        <p className="card-reveal__sub">is out!</p>
      </div>
    </div>
  );
}
